/**
 * Pure row helpers for the Quote Details gear editor — grouping/ordering,
 * client-key minting, and substitution classification. Extracted from
 * GearListSection.tsx (#81) so the row logic is testable without mounting the
 * component. No React, no component closure.
 */
import {
  isAvailabilityControlledItem,
  normalizeBookingItemType,
} from "./booking-item-types";
import {
  FALLBACK_QUOTE_GROUP,
  type GearItem,
  type GearItemInput,
  type GearRowClassification,
} from "./gear-list-types";

/**
 * Fill the grouped-quote fields pre-#21 callers and fixtures may omit, and
 * mint a client key. The one place rows entering the editor from props are
 * given their editable shape — mount seeding and the logistics re-seed below
 * must agree, or the two would compare unequal forever.
 */
export function normalizeRowDefaults(row: GearItemInput): GearItem {
  return {
    ...row,
    clientKey: row.clientKey || newClientKey(),
    quote_group: row.quote_group?.trim() || FALLBACK_QUOTE_GROUP,
    discount: row.discount && row.discount > 0 ? row.discount : 0,
    quote_group_summarised: row.quote_group_summarised ?? false,
    package_key: (row.package_key ?? "").trim() || null,
  };
}

/**
 * Normalise rows into grouped display order: groups by first appearance,
 * rows keeping their relative order within a group, sort_order renumbered to
 * the flat display index. This IS the persisted order, so the editor and
 * generation agree on row ordering (staleness compares rows by content, so
 * re-grouping alone never reads stale).
 */
export function normalizeGroupedRows(rows: GearItem[]): GearItem[] {
  const groupIdx = new Map<string, number>();
  for (const row of rows) {
    if (!groupIdx.has(row.quote_group))
      groupIdx.set(row.quote_group, groupIdx.size);
  }
  return rows
    .map((row, i) => ({ row, i }))
    .sort((a, b) => {
      const ga = groupIdx.get(a.row.quote_group)!;
      const gb = groupIdx.get(b.row.quote_group)!;
      return ga !== gb ? ga - gb : a.i - b.i;
    })
    .map(({ row }, i) => ({ ...row, sort_order: i }));
}

/**
 * The editor's half of "the server owns logistics rows" (#243 sweep 2 D4).
 *
 * A transport compute creates, reprices, and deletes the priced Transport
 * lines server-side; the gear PUT never inserts or deletes one. So whenever a
 * workspace read brings a DIFFERENT set of logistics rows, the editor swaps
 * its logistics subset for the server's and leaves every client-owned row
 * (and its unsaved edits) exactly where it is. This is what the pre-D4
 * refetch-AND-remount protocol was for — except a remount also discarded the
 * Operator's in-progress gear edits, and forgetting it silently wiped the
 * transport lines on the next autosave.
 *
 * Positions are preserved: a surviving row is replaced in place, new rows are
 * appended. Returns `rows` unchanged when the logistics subset already
 * matches, so an ordinary refetch causes no render and no autosave.
 */
export function reseedLogisticsRows(
  rows: GearItem[],
  serverLogistics: GearItem[],
): GearItem[] {
  const isLogistics = (r: GearItem) =>
    normalizeBookingItemType(r.type) === "logistics";
  const byID = new Map(serverLogistics.map((r) => [r.id, r]));
  const used = new Set<string | undefined>();
  const next: GearItem[] = [];
  for (const row of rows) {
    if (!isLogistics(row)) {
      next.push(row);
      continue;
    }
    const fresh = byID.get(row.id);
    if (!fresh) continue; // deleted server-side (recompute, last vehicle gone)
    used.add(row.id);
    // Keep the row's clientKey so React does not remount the row it replaces.
    next.push({ ...fresh, clientKey: row.clientKey });
  }
  for (const fresh of serverLogistics) {
    if (!used.has(fresh.id)) next.push({ ...fresh, clientKey: newClientKey() });
  }
  return sameLogisticsRows(rows, next) ? rows : next;
}

/** Value-compare of two row lists' logistics subsets (clientKey excluded). */
function sameLogisticsRows(a: GearItem[], b: GearItem[]): boolean {
  const project = (rows: GearItem[]) =>
    JSON.stringify(
      rows
        .filter((r) => normalizeBookingItemType(r.type) === "logistics")
        .map(({ clientKey: _clientKey, ...rest }) => rest),
    );
  return project(a) === project(b);
}

export function newClientKey(): string {
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
  ) {
    return crypto.randomUUID();
  }
  return `k-${Math.random().toString(36).slice(2)}-${Date.now()}`;
}

// pick_gear_for_roles writes `substituted_from_equipment_id` on every row where
// the picked SKU differs from the role's preferred — but that flag alone can't
// tell split-fulfillment (multiple SKUs combined to meet quantity) from true
// substitution (preferred unavailable, swapped for one alternative). Both look
// like "substituted" rows. We distinguish from the row set:
//
//   - "split": at least one OTHER row in the booking either uses this row's
//     preferred as its equipment_id (preferred took partial + chain took rest),
//     or shares this row's preferred as its own substituted_from (multi-sub
//     split across alternatives, preferred unused).
//   - "substituted": exactly one row points at this preferred, nobody else.
//   - "preferred": no substitution; pure preferred row not part of a split.
//
// Preferred-as-partial rows in a split (substituted_from = null but their
// equipment_id is referenced by another row's substituted_from) classify as
// "split" too, so every row in the split shares the same label.
export function classifyGearRows(
  items: Pick<GearItem, "equipment_id" | "substituted_from_equipment_id">[],
): GearRowClassification[] {
  return items.map((row, idx) => {
    const sub = row.substituted_from_equipment_id;
    if (!sub) {
      const eqId = row.equipment_id;
      if (!eqId) return "preferred";
      const someoneElseSubstitutedFromUs = items.some(
        (r, i) => i !== idx && r.substituted_from_equipment_id === eqId,
      );
      return someoneElseSubstitutedFromUs ? "split" : "preferred";
    }
    const sharesPreferred = items.some(
      (r, i) =>
        i !== idx &&
        (r.equipment_id === sub || r.substituted_from_equipment_id === sub),
    );
    return sharesPreferred ? "split" : "substituted";
  });
}

/** The banner's payload when a booking's gear sits at a stale hire window. */
export interface DateShift {
  bookingStart: string;
  bookingEnd: string;
  rowStart: string;
  rowEnd: string;
}

/**
 * Whether the booking's gear is still reserved for the dates it was picked
 * for. Returns null when nothing is stale — the banner's whole condition.
 *
 * A row is stale when it starts on the wrong day, or ends before the event
 * does. A row ending AFTER the event is not stale: since #227 the Days column
 * extends the row's own window, and a hire that outlasts the event (bump-out,
 * a two-day hold on a one-day show) is deliberate.
 *
 * Only availability-controlled (gear) rows are considered. Labour and
 * transport rows are single-day BY DESIGN — a service row carries total hours
 * in its quantity, so proposal.Apply pins ends_on to starts_on to stop a
 * multi-day window multiplying hours already counted. Judging them by the
 * event's end date made every multi-day booking announce that its dates had
 * changed the moment it was generated, on a booking nobody had edited.
 */
export function computeDateShift(
  items: Pick<GearItem, "type" | "starts_on" | "ends_on">[],
  eventDate: string | null | undefined,
  eventEndDate: string | null | undefined,
): DateShift | null {
  if (!eventDate || items.length === 0) return null;
  const expectedEnd = eventEndDate || eventDate;
  const stale = items.find(
    (it) =>
      isAvailabilityControlledItem(it.type) &&
      it.starts_on != null &&
      (it.starts_on !== eventDate || (it.ends_on ?? eventDate) < expectedEnd),
  );
  if (!stale) return null;
  return {
    bookingStart: eventDate,
    bookingEnd: expectedEnd,
    rowStart: stale.starts_on!,
    rowEnd: stale.ends_on ?? stale.starts_on!,
  };
}

/**
 * The Equipment ids whose quantity is spread over more than one Warehouse
 * (#304).
 *
 * The picker splits a Role across sites on purpose and MergeDuplicateGearItems
 * keeps those rows apart, so the editor shows the same item twice with no
 * visible reason. Only the rows in this set are annotated: every row carries a
 * Warehouse, so naming it on all of them would say nothing. Rows with no
 * Warehouse — manual ones, and everything written before migration 128 — never
 * qualify.
 */
export function equipmentSplitAcrossWarehouses(
  items: Pick<GearItem, "equipment_id" | "warehouse_id">[],
): Set<string> {
  const byEquipment = new Map<string, Set<string>>();
  for (const it of items) {
    if (!it.equipment_id || !it.warehouse_id) continue;
    const seen = byEquipment.get(it.equipment_id) ?? new Set<string>();
    seen.add(it.warehouse_id);
    byEquipment.set(it.equipment_id, seen);
  }

  const split = new Set<string>();
  for (const [equipmentId, warehouseIds] of byEquipment) {
    if (warehouseIds.size > 1) split.add(equipmentId);
  }
  return split;
}
